import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

interface HiddenModeContextType {
  isHiddenModeActive: boolean;
  showHiddenItems: () => void;
  hideHiddenItems: () => void;
}

const HiddenModeContext = createContext<HiddenModeContextType | undefined>(undefined);

export const useHiddenMode = () => {
  const context = useContext(HiddenModeContext);
  if (!context) {
    throw new Error('useHiddenMode must be used within a HiddenModeProvider');
  }
  return context;
};

interface HiddenModeProviderProps {
  children: React.ReactNode;
}

export const HiddenModeProvider: React.FC<HiddenModeProviderProps> = ({ children }) => {
  const [isHiddenModeActive, setIsHiddenModeActive] = useState(false);
  const [autoHideTimer, setAutoHideTimer] = useState<NodeJS.Timeout | null>(null);

  // Auto-hide after 5 minutes
  const startAutoHideTimer = useCallback(() => {
    if (autoHideTimer) {
      clearTimeout(autoHideTimer);
    }
    
    const timer = setTimeout(() => {
      setIsHiddenModeActive(false);
      setAutoHideTimer(null);
    }, 5 * 60 * 1000); // 5 minutes
    
    setAutoHideTimer(timer);
  }, [autoHideTimer]);

  const showHiddenItems = useCallback(() => {
    setIsHiddenModeActive(true);
    startAutoHideTimer();
  }, [startAutoHideTimer]);

  const hideHiddenItems = useCallback(() => {
    setIsHiddenModeActive(false);
    if (autoHideTimer) {
      clearTimeout(autoHideTimer);
      setAutoHideTimer(null);
    }
  }, [autoHideTimer]);

  // Keyboard event handlers
  useEffect(() => {
    let ctrlPressCount = 0;
    let ctrlTimer: NodeJS.Timeout | null = null;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Show hidden items: Ctrl+Shift+6 (removed Alt requirement)
      // Try both event.key and event.code for the number 6
      const isNumber6 = event.key === '6' || event.code === 'Digit6' || event.key === '^';
      if (event.ctrlKey && event.shiftKey && isNumber6 && !event.altKey) {
        event.preventDefault();
        showHiddenItems();
        return;
      }

      // Hide hidden items: Double Ctrl press
      if (event.key === 'Control') {
        ctrlPressCount++;
        
        if (ctrlPressCount === 1) {
          // Start timer for second press
          ctrlTimer = setTimeout(() => {
            ctrlPressCount = 0; // Reset if second press doesn't come within 500ms
          }, 500);
        } else if (ctrlPressCount === 2) {
          // Double Ctrl detected!
          if (ctrlTimer) {
            clearTimeout(ctrlTimer);
          }
          ctrlPressCount = 0;
          hideHiddenItems();
        }
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      // Reset ctrl count when other keys are pressed
      if (event.key !== 'Control') {
        ctrlPressCount = 0;
        if (ctrlTimer) {
          clearTimeout(ctrlTimer);
          ctrlTimer = null;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
      if (autoHideTimer) {
        clearTimeout(autoHideTimer);
      }
      if (ctrlTimer) {
        clearTimeout(ctrlTimer);
      }
    };
  }, [showHiddenItems, hideHiddenItems, autoHideTimer]);

  // Reset on page refresh (component mount)
  useEffect(() => {
    setIsHiddenModeActive(false);
    if (autoHideTimer) {
      clearTimeout(autoHideTimer);
      setAutoHideTimer(null);
    }
  }, []);

  const value = {
    isHiddenModeActive,
    showHiddenItems,
    hideHiddenItems
  };

  return (
    <HiddenModeContext.Provider value={value}>
      {children}
    </HiddenModeContext.Provider>
  );
};
